import type { ActionDef, BootstrapPayload, MenuEntry, OrganizationRef, ViewMode } from "../core";

// ── Organization tree (hierarchical from a flat allowed-org list) ─────────────
export interface OrgNode extends OrganizationRef {
    children: OrgNode[];
}

export function buildOrgTree(orgs: OrganizationRef[]): OrgNode[] {
    const byId = new Map<string, OrgNode>();
    for (const org of orgs) byId.set(org.id, { ...org, children: [] });
    const roots: OrgNode[] = [];
    for (const node of byId.values()) {
        const parent = node.parentId ? byId.get(node.parentId) : undefined;
        if (parent) parent.children.push(node);
        else roots.push(node); // orphan-promotion: parent outside the allowed set → treat as root
    }
    return roots;
}

// ── App search index (apps + their menu leaves, with breadcrumb paths) ────────
export interface AppSearchItem {
    appId: string;
    actionKey?: string;
    path: string[];
}

export function buildAppSearchIndex(boot: BootstrapPayload): AppSearchItem[] {
    const out: AppSearchItem[] = [];
    for (const app of boot.apps) {
        out.push({ appId: app.id, path: [app.name] });
        const walk = (menus: MenuEntry[], trail: string[]) => {
            for (const menu of menus) {
                const here = [...trail, menu.name];
                if (menu.actionKey) out.push({ appId: app.id, actionKey: menu.actionKey, path: [app.name, ...here] });
                if (menu.children?.length) walk(menu.children, here);
            }
        };
        walk(boot.menus[app.id] ?? [], []);
    }
    return out;
}

// Working vs off-hours for an org's timezone (default 09:00–18:00, Mon–Fri),
// plus the current local time. Returns null when no timezone is known.
export function workingStatus(tz?: string): { state: "working" | "offhours"; localTime: string } | null {
    if (!tz) return null;
    try {
        const parts = new Intl.DateTimeFormat("en-GB", {
            timeZone: tz,
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
            weekday: "short",
        }).formatToParts(new Date());
        const hour = Number(parts.find((p) => p.type === "hour")?.value);
        const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
        const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
        const weekend = weekday === "Sat" || weekday === "Sun";
        const working = !weekend && hour >= 9 && hour < 18;
        return { state: working ? "working" : "offhours", localTime: `${String(hour).padStart(2, "0")}:${minute}` };
    } catch {
        return null;
    }
}

export function initialsOf(name: string): string {
    // Strip parenthesized / bracketed annotations like "(Demo)" or "[draft]"
    // BEFORE splitting — those are noise for avatars, not part of the entity
    // name (otherwise "Globex Inc. (Demo)" → "G(" because "(Demo)" wins).
    const cleaned = name
        .replace(/\([^)]*\)/g, " ")
        .replace(/\[[^\]]*\]/g, " ")
        .trim();
    // Pull only word tokens that START with a letter or digit — drops
    // residue like "Inc." -> "Inc" still counts, but ".org" -> drops the dot
    // when measuring the first character.
    const words = cleaned
        .split(/\s+/)
        .map((w) => w.replace(/^[^\p{L}\p{N}]+/u, ""))
        .filter((w) => w.length > 0 && /[\p{L}\p{N}]/u.test(w[0]));
    if (words.length === 0) return "?";
    if (words.length === 1) {
        // Single word — take first 2 letters/digits (skip punctuation).
        const chars = words[0].replace(/[^\p{L}\p{N}]/gu, "");
        return chars.slice(0, 2).toUpperCase() || "?";
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function firstActionKey(menus: MenuEntry[]): string | undefined {
    for (const menu of menus) {
        if (menu.actionKey) return menu.actionKey;
        if (menu.children?.length) {
            const nested = firstActionKey(menu.children);
            if (nested) return nested;
        }
    }
    return undefined;
}

export interface ResolvedAction {
    actionKey: string;
    // undefined for client actions (no view mode → no URL segment).
    viewMode?: ViewMode;
}

// The view mode an action should open on. Client actions (clientComponent set)
// have none. Window actions honour their backend-resolved default view (which
// already falls back to the first available view), then "list".
function actionViewMode(action: MenuEntry["action"]): ViewMode | undefined {
    if (!action) return "list";
    if (action.clientComponent) return undefined;
    return action.defaultView ?? "list";
}

// First navigable action in a menu tree (depth-first), carrying its default view.
export function firstMenuAction(menus: MenuEntry[]): ResolvedAction | undefined {
    for (const menu of menus) {
        if (menu.actionKey) return { actionKey: menu.actionKey, viewMode: actionViewMode(menu.action) };
        if (menu.children?.length) {
            const nested = firstMenuAction(menu.children);
            if (nested) return nested;
        }
    }
    return undefined;
}

// The ActionDef bound to `actionKey` anywhere in the boot menus, or undefined.
// Lets OPEN_ACTION resolve both whether the target is a client action (drop the
// view-mode segment) AND its configured default view (so a call site that opens
// the action without an explicit viewMode — e.g. the app switcher — still lands
// on the action's own default, kanban for the opportunity pipeline, not "list").
export function findActionInBoot(boot: BootstrapPayload, actionKey: string): ActionDef | undefined {
    for (const menus of Object.values(boot.menus)) {
        const action = findMenuAction(menus, actionKey);
        if (action) return action;
    }
    return undefined;
}

// The landing action: the first app's first menu action (apps are sequence-sorted).
export function firstAppAction(boot: BootstrapPayload): ResolvedAction | undefined {
    for (const app of boot.apps) {
        const found = firstMenuAction(boot.menus[app.id] ?? []);
        if (found) return found;
    }
    return undefined;
}

export function menuContains(menu: MenuEntry, actionKey: string): boolean {
    if (menu.actionKey === actionKey) return true;
    return !!menu.children?.some((child) => menuContains(child, actionKey));
}

export function findCurrentAppId(boot: BootstrapPayload, actionKey: string): string | undefined {
    for (const app of boot.apps) {
        const menus = boot.menus[app.id] ?? [];
        if (menus.some((m) => menuContains(m, actionKey))) return app.id;
    }
    return undefined;
}

// The action descriptor (incl. availableViews) of the menu leaf for an actionKey.
export function findMenuAction(menus: MenuEntry[], actionKey: string): ActionDef | undefined {
    for (const menu of menus) {
        if (menu.actionKey === actionKey && menu.action) return menu.action;
        if (menu.children?.length) {
            const nested = findMenuAction(menu.children, actionKey);
            if (nested) return nested;
        }
    }
    return undefined;
}
